import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { CheckCircle2, Loader2, GraduationCap, Mail, AlertCircle, ArrowRight } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { authService } from "@/services/authService";
import { SearchablePicklist } from "@/components/ui/searchable-picklist";
import { parseThrottleSeconds } from "@/utils/helpers";

type RegistrationStep = "email" | "form" | "verification-sent";

interface LocationState {
    verifyEmail?: string;
    expiresIn?: number | null;
}

const Register = () => {
    const { branding, loading } = useBranding();
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as LocationState | null;

    // Initialize step based on whether we're coming from login redirect
    const [step, setStep] = useState<RegistrationStep>(() => 
        locationState?.verifyEmail ? "verification-sent" : "email"
    );
    const [email, setEmail] = useState("");
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [termsError, setTermsError] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [timeLeft, setTimeLeft] = useState(900); // Countdown timer in seconds (15 min)
    // null = fresh registration (use default), 0 = expired token from login, >0 = remaining time from login
    const initialTimeLeftRef = useRef<number | null>(
        locationState?.verifyEmail 
            ? (locationState.expiresIn && locationState.expiresIn > 0 ? locationState.expiresIn : 0)
            : null
    );
    const [canResend, setCanResend] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [timerKey, setTimerKey] = useState(0); // Increment to restart timer
    const [throttleCooldown, setThrottleCooldown] = useState(0);
    const [formData, setFormData] = useState(() => ({
        firstName: "",
        lastName: "",
        country: "",
        email: locationState?.verifyEmail || "",
        password: "",
        confirmPassword: "",
    }));
    const [countries, setCountries] = useState<string[]>([]);
    const [fieldErrors, setFieldErrors] = useState<{ firstName: string | null; lastName: string | null }>({ firstName: null, lastName: null });
    const [countryError, setCountryError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

    const firstNameRef = useRef<HTMLInputElement>(null);
    const lastNameRef = useRef<HTMLInputElement>(null);
    const countryRef = useRef<HTMLButtonElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const confirmPasswordRef = useRef<HTMLInputElement>(null);

    const primaryColor = branding?.branding?.colors?.primary || "#1d3658";

    // Clear location state after reading it (prevent re-trigger on refresh)
    useEffect(() => {
        if (locationState?.verifyEmail) {
            navigate(location.pathname, { replace: true, state: null });
        }
    }, []);

    useEffect(() => {
        fetch('/api/integrations/hub/countries/')
            .then((res) => res.json())
            .then((data) => {
                if (data.status === 'success' && data.countries?.length > 0) {
                    setCountries(data.countries);
                }
            })
            .catch((err) => console.error('Failed to fetch countries:', err));
    }, []);

    /**
     * Advance from the email step straight to the form.
     *
     * There is deliberately no lookup here any more. Asking the backend whether
     * this address is already registered — and rendering anything different when
     * it is — made an account-existence oracle out of an endpoint that has to be
     * unauthenticated, so the backend no longer answers the question (see
     * SearchApplicantView). A duplicate is caught on submit instead, where
     * register returns 409 `already_exists` and `handleSubmit` says so — after
     * the user has demonstrably typed their own details, not on a bare address.
     */
    const handleEmailSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setFormData({ ...formData, email });
        setStep("form");
    };

    const handleNameChange = (field: "firstName" | "lastName", value: string) => {
        const normalized = value.normalize("NFC");
        const hasInvalidChars = /[^\p{L}\s\-\u2019']/u.test(normalized);
        const cleaned = normalized.replace(/[^\p{L}\s\-\u2019']/gu, "");
        const codePoints = Array.from(cleaned);
        const isTruncated = codePoints.length > 40;
        const sanitized = codePoints.slice(0, 40).join("");

        if (hasInvalidChars) {
            setFieldErrors((prev) => ({ ...prev, [field]: "Only letters, spaces, hyphens, and apostrophes are allowed." }));
        } else if (isTruncated) {
            setFieldErrors((prev) => ({ ...prev, [field]: "Maximum 40 characters allowed." }));
        } else {
            setFieldErrors((prev) => ({ ...prev, [field]: null }));
        }

        setFormData((prev) => ({ ...prev, [field]: sanitized }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsPending(true);
        setError(null);

        // Validate all fields
        const nameErrors = { firstName: null as string | null, lastName: null as string | null };
        if (!formData.firstName.trim()) nameErrors.firstName = "First name is required.";
        if (!formData.lastName.trim()) nameErrors.lastName = "Last name is required.";
        const hasCountryError = !formData.country;
        const pwdError = !formData.password
            ? "Password is required."
            : formData.password.length < 8
            ? "Password must be at least 8 characters."
            : null;
        const confirmPwdError = !formData.confirmPassword
            ? "Please confirm your password."
            : formData.password !== formData.confirmPassword
            ? "Passwords do not match."
            : null;

        if (nameErrors.firstName || nameErrors.lastName) setFieldErrors(nameErrors);
        if (hasCountryError) setCountryError("Please select your citizenship.");
        if (pwdError) setPasswordError(pwdError);
        if (confirmPwdError) setConfirmPasswordError(confirmPwdError);

        if (nameErrors.firstName || nameErrors.lastName || hasCountryError || pwdError || confirmPwdError) {
            setIsPending(false);
            // Scroll to first invalid field
            if (nameErrors.firstName) firstNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (nameErrors.lastName) lastNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (hasCountryError) countryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (pwdError) passwordRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            else if (confirmPwdError) confirmPasswordRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }

        if (!agreedToTerms) {
            setTermsError(true);
            setIsPending(false);
            return;
        }

        try {
            await authService.register({
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email,
                password: formData.password,
                citizenship: formData.country,
            });

            setStep("verification-sent");
        } catch (error: any) {
            console.error("Registration error:", error);
            
            // DRF returns field errors directly on response.data (e.g., {password: ["..."]})
            const data = error.response?.data;
            let errorMsg = "Registration failed. Please try again.";
            
            if (data?.password) {
                // Password validation errors (common password, too short, etc.)
                errorMsg = Array.isArray(data.password) ? data.password.join(" ") : data.password;
            } else if (data?.email) {
                errorMsg = Array.isArray(data.email) ? data.email.join(" ") : data.email;
            } else if (data?.error) {
                // Map known error codes to user-friendly messages
                switch (data.error) {
                    // Covers a duplicate at THIS institution and at any other one:
                    // RegisterView answers both with `already_exists` and nothing
                    // else, so the message must not speculate about where. The old
                    // `email_exists_elsewhere` branch named the other institutions,
                    // which is the cross-tenant leak the backend closed.
                    case "already_exists":
                        errorMsg = "An account with this email already exists. Please log in instead.";
                        break;
                    case "invalid_code":
                        errorMsg = "Invalid or expired verification code. Please try again.";
                        break;
                    case "email_not_verified":
                        errorMsg = "Please verify your email before logging in.";
                        break;
                    default:
                        // Use message if provided, otherwise fallback to generic
                        errorMsg = data.message || "Registration failed. Please try again.";
                }
            } else if (data?.detail) {
                errorMsg = data.detail;
            } else if (data?.non_field_errors) {
                errorMsg = Array.isArray(data.non_field_errors) ? data.non_field_errors.join(" ") : data.non_field_errors;
            }
            
            setError(errorMsg);
        } finally {
            setIsPending(false);
        }
    };

    const handleVerifyCode = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsPending(true);
        setError(null);

        try {
            const data = await authService.verifyCode(formData.email, verificationCode);

            if (data.status === 'verified') {
                // Update auth context with user data (tokens already stored by authService)
                if (data.user) {
                    setUser(data.user);
                }
                // Navigate to dashboard or home
                navigate('/portal', { 
                    state: { message: 'Email verified and logged in successfully!' }
                });
            } else {
                setError(data.message || 'Invalid verification code.');
            }
        } catch (error: any) {
            console.error('Verification error:', error);
            
            // Handle axios error response
            const data = error.response?.data;

            if (error.response?.status === 429) {
                setError(null);
                setThrottleCooldown(parseThrottleSeconds(error.response?.data?.detail));
            } else {
                let errorMsg = 'Failed to verify code. Please try again.';
                if (data?.error === 'invalid_code') {
                    errorMsg = 'Incorrect code. Please check your email and try again.';
                } else if (data?.error === 'account_disabled') {
                    errorMsg = 'Your account has been disabled. Please contact support.';
                } else if (data?.message) {
                    errorMsg = data.message;
                } else if (data?.detail) {
                    errorMsg = data.detail;
                }
                setError(errorMsg);
            }
        } finally {
            setIsPending(false);
        }
    };

    // Throttle countdown timer
    useEffect(() => {
        if (throttleCooldown <= 0) return;
        const timer = window.setTimeout(() => {
            setThrottleCooldown((prev) => Math.max(0, prev - 1));
        }, 1000);
        return () => window.clearTimeout(timer);
    }, [throttleCooldown]);

    // Countdown timer effect for verification code expiry
    useEffect(() => {
        if (step !== "verification-sent") return;
        
        // Use initial time from login redirect if available, otherwise default to 900s (15 min)
        const startTime = initialTimeLeftRef.current ?? 900;
        initialTimeLeftRef.current = null; // Clear after use
        setTimeLeft(startTime);
        setCanResend(startTime <= 0);
        
        if (startTime <= 0) return; // Already expired, don't start timer
        
        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    setCanResend(true);
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [step, timerKey]);

    const handleResendCode = async () => {
        setIsResending(true);
        setError(null);
        
        try {
            const data = await authService.resendVerificationCode(formData.email);
            // Seed the countdown from the server's own window. Resend re-sends the
            // code the user already has rather than minting a new one, so its
            // remaining life is shorter than a fresh 15 minutes — assuming 900 here
            // would run the timer past the code's death, leaving the user unable to
            // verify and unable to resend until a full page reload.
            initialTimeLeftRef.current =
                typeof data?.expires_in === "number" ? data.expires_in : null;
            // Restart timer by incrementing key (triggers useEffect)
            setTimerKey((prev) => prev + 1);
            setVerificationCode(""); // Clear any entered code
        } catch (error: any) {
            console.error('Resend error:', error);
            const data = error.response?.data;
            if (data?.error === 'already_verified') {
                setError('Your email is already verified. Please log in.');
            } else {
                setError('Failed to resend code. Please try again.');
            }
        } finally {
            setIsResending(false);
        }
    };

    // Render step: Verification email sent
    if (step === "verification-sent") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50/50 p-4 font-sans text-foreground">
                <div className="w-full max-w-md relative z-10">
                    <Card className="border-none shadow-2xl overflow-hidden bg-white/80 backdrop-blur-sm">
                        <CardContent className="p-8 text-center space-y-6">
                            <div className="flex justify-center">
                                <div className="h-16 w-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center">
                                    <CheckCircle2 size={32} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <h2 className="text-2xl font-serif font-bold text-slate-900">
                                    Check your email
                                </h2>
                                <p className="text-muted-foreground text-sm">
                                    We've sent a verification code to{" "}
                                    <span className="font-bold text-slate-900">
                                        {formData.email}
                                    </span>
                                </p>
                            </div>
                            <form
                                onSubmit={handleVerifyCode}
                                className="space-y-4 text-left"
                            >
                                <div className="space-y-2">
                                    <Label htmlFor="verification-code">Verification Code</Label>
                                    <Input
                                        id="verification-code"
                                        type="text"
                                        placeholder="Enter your 6-character code"
                                        className={`h-11 bg-white text-center tracking-widest text-lg font-mono uppercase placeholder:text-xs placeholder:tracking-normal placeholder:font-sans placeholder:normal-case ${error && throttleCooldown <= 0 ? 'border-red-500 focus:border-red-500' : ''}`}
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                        data-testid="input-verification-code"
                                        value={verificationCode}
                                        onChange={(event) => {
                                            setVerificationCode(event.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase());
                                            setError(null); // Clear error when user types
                                        }}
                                    />
                                    {throttleCooldown > 0 ? (
                                        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-center justify-between mt-2">
                                            <span>Too many attempts. Try again in</span>
                                            <span className="font-mono font-bold">{String(Math.floor(throttleCooldown / 60)).padStart(2, '0')}:{String(throttleCooldown % 60).padStart(2, '0')}</span>
                                        </div>
                                    ) : error ? (
                                        <p className="text-red-500 text-sm mt-1">
                                            {error}
                                        </p>
                                    ) : null}
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full text-white font-bold h-12 rounded-xl shadow-lg"
                                    style={{ backgroundColor: primaryColor }}
                                    disabled={isPending || verificationCode.length !== 6 || throttleCooldown > 0}
                                >
                                    {isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Verifying...
                                        </>
                                    ) : (
                                        "Verify Code"
                                    )}
                                </Button>
                            </form>
                            
                            {/* Timer and Resend section */}
                            <div className="pt-4 border-t border-slate-200">
                                {!canResend ? (
                                    <p className="text-sm text-muted-foreground">
                                        Code expires in <span className="font-bold text-slate-900">{String(Math.floor(timeLeft / 60)).padStart(2, '0')}:{String(timeLeft % 60).padStart(2, '0')}</span>
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        <p className="text-sm text-amber-600">
                                            Code expired
                                        </p>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full"
                                            onClick={handleResendCode}
                                            disabled={isResending}
                                        >
                                            {isResending ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                "Resend Code"
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Main render - Multi-step registration
    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50/50 p-4 font-sans text-foreground">
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-secondary/10 rounded-full blur-3xl" />
            </div>

            <div className="w-full max-w-md relative z-10">
                <div className="flex flex-col items-center mb-8">
                    <Link
                        to="/"
                        className="flex flex-col items-center gap-3 mb-4 group text-center"
                    >
                        <div className="w-20 h-20 flex items-center justify-center">
                            {!loading && branding?.branding?.logo ? (
                                <img
                                    src={branding.branding.logo}
                                    alt={`${branding.name} Logo`}
                                    className="max-w-full max-h-full object-contain"
                                />
                            ) : (
                                <div
                                    className="w-full h-full rounded-lg flex items-center justify-center text-white"
                                    style={{
                                        backgroundColor:
                                            branding?.branding?.colors?.primary || "#1d3658",
                                    }}
                                >
                                    <GraduationCap className="w-12 h-12" />
                                </div>
                            )}
                        </div>
                        <span
                            className="font-serif text-3xl font-black text-primary text-center"
                            style={{
                                fontFamily: "var(--font-university-name)",
                            }}
                        >
                            {!loading && branding?.name ? branding.name : "University Portal"}
                        </span>
                    </Link>
                    <h1 className="text-xl font-bold text-slate-900">Create Account</h1>
                </div>

                <Card className="border-none shadow-2xl overflow-hidden bg-white/80 backdrop-blur-sm">
                    <CardContent className="p-8">
                        {/* Step 1: Email Entry */}
                        {step === "email" && (
                            <form onSubmit={handleEmailSearch} className="space-y-4">
                                <div className="text-center space-y-2 mb-6">
                                    <h2 className="text-2xl font-serif font-bold text-slate-900">
                                        Let's get started
                                    </h2>
                                    <p className="text-muted-foreground text-sm">
                                        Enter your email to begin your application
                                    </p>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email Address</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="your.email@example.com"
                                            className="h-11 pl-10 bg-white"
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                                        <p className="text-sm text-red-800">{error}</p>
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full text-white font-bold h-12 rounded-xl shadow-lg mt-2"
                                    style={{ backgroundColor: primaryColor }}
                                    disabled={isPending}
                                >
                                    {isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                            Checking...
                                        </>
                                    ) : (
                                        <>
                                            Continue
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </form>
                        )}

                        {/* Step 2c: Registration Form */}
                        {step === "form" && (
                            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                                <div className="text-center space-y-2 mb-6">
                                    <h2 className="text-2xl font-serif font-bold text-slate-900">
                                        Complete Your Profile
                                    </h2>
                                    <p className="text-muted-foreground text-sm">
                                        Fill in your details to create your account
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="firstName"
                                            placeholder="James"
                                            className={`h-11 bg-white ${fieldErrors.firstName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                            value={formData.firstName}
                                            onChange={(event) => handleNameChange("firstName", event.target.value)}
                                            maxLength={40}
                                            ref={firstNameRef}
                                            data-testid="input-firstName"
                                        />
                                        {fieldErrors.firstName && (
                                            <p className="text-xs text-red-500">{fieldErrors.firstName}</p>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="lastName"
                                            placeholder="Smith"
                                            className={`h-11 bg-white ${fieldErrors.lastName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                            value={formData.lastName}
                                            onChange={(event) => handleNameChange("lastName", event.target.value)}
                                            maxLength={40}
                                            ref={lastNameRef}
                                            data-testid="input-lastName"
                                        />
                                        {fieldErrors.lastName && (
                                            <p className="text-xs text-red-500">{fieldErrors.lastName}</p>
                                        )}
                                    </div>
                                </div>
                                <SearchablePicklist
                                    id="country"
                                    label="Citizenship"
                                    required
                                    options={countries}
                                    value={formData.country}
                                    onChange={(val) => { setFormData({ ...formData, country: val }); setCountryError(null); }}
                                    placeholder="Select your citizenship"
                                    searchPlaceholder="Search country..."
                                    error={countryError}
                                    buttonRef={countryRef}
                                    data-testid="input-country"
                                />
                                <div className="space-y-2">
                                    <Label htmlFor="form-email">Email Address <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="form-email"
                                        type="email"
                                        className="h-11 bg-slate-50 text-slate-500 cursor-not-allowed"
                                        value={formData.email}
                                        readOnly
                                        data-testid="input-email"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        className={`h-11 bg-white ${passwordError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                        value={formData.password}
                                        onChange={(event) => {
                                            setFormData({ ...formData, password: event.target.value });
                                            setPasswordError(null);
                                            if (confirmPasswordError) setConfirmPasswordError(null);
                                        }}
                                        ref={passwordRef}
                                        data-testid="input-password"
                                    />
                                    {passwordError ? (
                                        <p className="text-xs text-red-500">{passwordError}</p>
                                    ) : (
                                        <p className="text-xs text-slate-500">Password must be at least 8 characters</p>
                                    )}
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="confirmPassword">Confirm Password <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        placeholder="••••••••"
                                        className={`h-11 bg-white ${
                                            confirmPasswordError
                                                ? "border-red-500 focus-visible:ring-red-500"
                                                : ""
                                        }`}
                                        value={formData.confirmPassword}
                                        onChange={(event) => {
                                            setFormData({ ...formData, confirmPassword: event.target.value });
                                            setConfirmPasswordError(null);
                                        }}
                                        ref={confirmPasswordRef}
                                        data-testid="input-confirmPassword"
                                    />
                                    {confirmPasswordError && (
                                        <p className="text-xs text-red-500">{confirmPasswordError}</p>
                                    )}
                                </div>
                                <div className="flex items-start gap-3 pt-1">
                                    <input
                                        id="agreedToTerms"
                                        type="checkbox"
                                        checked={agreedToTerms}
                                        onChange={(event) => { setAgreedToTerms(event.target.checked); if (event.target.checked) setTermsError(false); }}
                                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-400 accent-current cursor-pointer"
                                        style={{ accentColor: primaryColor }}
                                        data-testid="checkbox-terms"
                                    />
                                    <label htmlFor="agreedToTerms" className="text-sm text-slate-600 cursor-pointer leading-snug">
                                        I agree to the{" "}
                                        {branding?.branding?.privacy_policy_url && branding?.branding?.terms_of_service_url ? (
                                            <>
                                                <a
                                                    href={branding.branding.privacy_policy_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-semibold hover:underline"
                                                    style={{ color: primaryColor }}
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    Privacy Policy
                                                </a>
                                                {" and "}
                                                <a
                                                    href={branding.branding.terms_of_service_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-semibold hover:underline"
                                                    style={{ color: primaryColor }}
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    Terms of Service
                                                </a>
                                            </>
                                        ) : branding?.branding?.privacy_policy_url ? (
                                            <>
                                                <a
                                                    href={branding.branding.privacy_policy_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-semibold hover:underline"
                                                    style={{ color: primaryColor }}
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    Privacy Policy
                                                </a>
                                                {" and "}
                                                <span className="font-semibold" style={{ color: primaryColor }} onClick={(event) => event.stopPropagation()}>Terms of Service</span>
                                            </>
                                        ) : branding?.branding?.terms_of_service_url ? (
                                            <>
                                                <span className="font-semibold" style={{ color: primaryColor }} onClick={(event) => event.stopPropagation()}>Privacy Policy</span>
                                                {" and "}
                                                <a
                                                    href={branding.branding.terms_of_service_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-semibold hover:underline"
                                                    style={{ color: primaryColor }}
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    Terms of Service
                                                </a>
                                            </>
                                        ) : (
                                            <span className="font-semibold" onClick={(event) => event.stopPropagation()}>
                                                Privacy Policy and Terms of Service
                                            </span>
                                        )}
                                        .
                                    </label>
                                </div>
                                {termsError && (
                                    <p className="text-xs text-red-500 flex items-center gap-1 -mt-1">
                                        <AlertCircle className="h-3 w-3 shrink-0" />
                                        Please check this box to create your account.
                                    </p>
                                )}
                                {error && (
                                    <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                                        <p className="text-sm text-red-800">{error}</p>
                                    </div>
                                )}
                                <Button
                                    type="submit"
                                    className="w-full text-white font-bold h-12 rounded-xl shadow-lg mt-2"
                                    style={{ backgroundColor: primaryColor }}
                                    disabled={isPending}
                                    data-testid="button-register"
                                >
                                    {isPending ? (
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                    ) : (
                                        "Create Account"
                                    )}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                    <CardFooter className="flex justify-center border-t border-slate-400 bg-slate-50/50 py-6">
                        <p className="text-sm text-slate-500">
                            Already have an account?{" "}
                            <Link
                                to="/login"
                                className="font-semibold hover:underline"
                                style={{ color: primaryColor }}
                                data-testid="link-login"
                            >
                                Log in here
                            </Link>
                        </p>
                    </CardFooter>
                </Card>
            </div>
        </div>
    );
};

export default Register;
